import { Plus, Save, Trash2 } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import EditIcon from '@/assets/store/icon/editprofile-icon.svg';
import type { Gender } from '@/domains/auth/types/auth.types';
import { birthDateError, emailError, usernameError } from '@/domains/auth/utils/validation';
import DateTimeMobile from '@/domains/mobile-web/components/DateTimeMobile/DateTimeMobile';
import FieldLabelMobile from '@/domains/mobile-web/components/FieldLabelMobile/FieldLabelMobile';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import RadioGroupMobile, {
  type RadioOptionMobile,
} from '@/domains/mobile-web/components/RadioGroupMobile/RadioGroupMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { fieldErrorOf } from '@/domains/mobile-web/hooks/useAddresses';
import { useProfile, useSaveProfile } from '@/domains/mobile-web/hooks/useProfile';
import type { Profile, ProfileBody } from '@/domains/mobile-web/types/profile.types';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import Button from '@/shared/components/ui/Button';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import { showToast } from '@/shared/utils/showToast';

const GENDERS: RadioOptionMobile<Gender>[] = [
  { value: 'male', label: 'Laki-Laki' },
  { value: 'female', label: 'Perempuan' },
];

const MAX_CHILDREN = 10;

const CHANGE_PHONE_ROUTE = '/store/edit-phone';

// gender is unset for an account made without the registration form (Google, admin)
interface PersonalDraft {
  name: string;
  gender?: Gender;
  date_of_birth: string;
  username: string;
  email: string;
}

// key only keeps React rows stable while children are removed; id is set for a saved child
interface ChildDraft {
  key: number;
  id?: string;
  name: string;
  gender: Gender;
  date_of_birth: string;
}

type Errors = Record<string, string | undefined>;

const validate = (form: PersonalDraft, children: ChildDraft[]): Errors => {
  const errors: Errors = {
    name: form.name.trim() ? undefined : 'Nama lengkap wajib diisi.',
    gender: form.gender ? undefined : 'Jenis kelamin wajib dipilih.',
    date_of_birth: birthDateError(form.date_of_birth),
    username: usernameError(form.username),
    email: emailError(form.email),
  };
  children.forEach((child, index) => {
    errors[`children.${index}.name`] = child.name.trim()
      ? undefined
      : 'Nama lengkap anak wajib diisi.';
    errors[`children.${index}.date_of_birth`] = birthDateError(child.date_of_birth);
  });
  return errors;
};

const EditProfile = () => {
  const navigate = useNavigate();
  const { data: profile, isLoading, isError } = useProfile();

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader type="back" title="Edit Profil" onBack={() => navigate(-1)} />
      </HeaderMobile>

      {isLoading ? null : isError || !profile ? (
        <WrapperMobile>
          <p className="body1 text-text-grey">Gagal memuat profil.</p>
        </WrapperMobile>
      ) : (
        <ProfileForm profile={profile} />
      )}
    </main>
  );
};

const ProfileForm = ({ profile }: { profile: Profile }) => {
  const navigate = useNavigate();
  const saveProfile = useSaveProfile();

  const [form, setForm] = useState<PersonalDraft>({
    name: profile.name ?? '',
    gender: profile.gender ?? undefined,
    date_of_birth: profile.date_of_birth ?? '',
    username: profile.username ?? '',
    email: profile.email ?? '',
  });
  const nextKey = useRef(profile.children.length);
  const [children, setChildren] = useState<ChildDraft[]>(
    profile.children.map((child, key) => ({ ...child, key }))
  );
  const [errors, setErrors] = useState<Errors>({});

  const setField = <K extends keyof PersonalDraft>(field: K, value: PersonalDraft[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const setChild = <K extends 'name' | 'gender' | 'date_of_birth'>(
    index: number,
    field: K,
    value: ChildDraft[K]
  ) => {
    setChildren((current) =>
      current.map((child, i) => (i === index ? { ...child, [field]: value } : child))
    );
    setErrors((current) => ({ ...current, [`children.${index}.${field}`]: undefined }));
  };

  const addChild = () => {
    setChildren((current) => [
      ...current,
      { key: nextKey.current++, name: '', gender: 'male', date_of_birth: '' },
    ]);
  };

  const removeChild = (index: number) => {
    setChildren((current) => current.filter((_, i) => i !== index));
    // Child errors are keyed by position, so they are recomputed on the next submit
    setErrors((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith('children.')))
    );
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = validate(form, children);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean) || !form.gender) return;

    const body: ProfileBody = {
      name: form.name.trim(),
      gender: form.gender,
      date_of_birth: form.date_of_birth,
      username: form.username.trim(),
      email: form.email.trim(),
      children: children.map(({ key: _key, ...child }) => ({ ...child, name: child.name.trim() })),
    };
    saveProfile.mutate(body, {
      onSuccess: () => {
        showToast({
          type: 'success',
          title: 'Profil disimpan',
          message: 'Informasi pribadi berhasil disimpan.',
        });
        navigate(-1);
      },
      onError: (error) => {
        const fieldError = fieldErrorOf(error);
        if (fieldError && fieldError.field in nextErrors) {
          setErrors({ [fieldError.field]: fieldError.message });
        } else {
          showToast({ type: 'error', title: 'Gagal menyimpan profil', message: error.message });
        }
      },
    });
  };

  return (
    <WrapperMobile>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
        <div className="shadowCard p-3 flex flex-col gap-3">
          <div className="inline-flex w-full gap-3 items-center">
            <figure className="shrink-0">
              <img src={EditIcon} alt="icon edit" loading="eager" className="w-auto h-auto" />
            </figure>
            <div className="flex-1 min-w-0">
              <p className="title2medium truncate">Pengaturan Informasi Pribadi</p>
              <p className="reg12 text-text-grey truncate">
                Atur informasi Pribadi, tanggal lahir dan lainnya.{' '}
              </p>
            </div>
          </div>
          <p className="body1bold text-black">Data Pribadi</p>
          <InputMobile
            label="Nama Lengkap"
            name="name"
            placeholder="Masukkan nama lengkap anda"
            autoComplete="name"
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            error={errors.name}
          />
          <RadioGroupMobile
            label="Jenis Kelamin"
            name="gender"
            options={GENDERS}
            value={form.gender}
            onChange={(value) => setField('gender', value)}
            error={errors.gender}
          />
          <DateTimeMobile
            label="Tanggal Lahir"
            name="date_of_birth"
            value={form.date_of_birth}
            onChange={(event) => setField('date_of_birth', event.target.value)}
            error={errors.date_of_birth}
          />
          <InputMobile
            label="Username"
            name="username"
            placeholder="Buat username anda"
            autoComplete="username"
            value={form.username}
            onChange={(event) => setField('username', event.target.value)}
            error={errors.username}
          />
          <InputMobile
            label="Alamat Email"
            name="email"
            type="email"
            placeholder="Masukkan alamat email anda"
            autoComplete="email"
            value={form.email}
            onChange={(event) => setField('email', event.target.value)}
            error={errors.email}
          />
          <FieldLabelMobile label="Nomor HP" htmlFor="phone_number">
            <div className="relative w-full">
              <input
                id="phone_number"
                name="phone_number"
                type="tel"
                value={profile.phone_number ?? ''}
                disabled
                className="field-mobile pr-20"
              />
              {/* Changed only with a verification code, on its own page */}
              <Link
                to={CHANGE_PHONE_ROUTE}
                aria-label={profile.phone_number ? 'Ubah nomor HP' : 'Tambah nomor HP'}
                className="absolute right-4 top-1/2 -translate-y-1/2 body1medium text-text-blue-100"
              >
                {profile.phone_number ? 'Ubah' : 'Tambah'}
              </Link>
            </div>
          </FieldLabelMobile>
          <div className="h-px w-full bg-blue-cs-200" />
          <p className="body1bold text-black">Informasi Anak</p>
          {children.map((child, index) => (
            <div
              key={child.key}
              className="flex flex-col gap-4 rounded-[12px] border border-blue-cs-200 p-4"
            >
              <div className="flex items-center justify-between">
                <p className="title2medium text-black">Anak {index + 1}</p>
                <button
                  type="button"
                  onClick={() => removeChild(index)}
                  aria-label={`Hapus anak ${index + 1}`}
                  className="text-text-danger-500"
                >
                  <Trash2 className="size-5" />
                </button>
              </div>
              <InputMobile
                label="Nama Lengkap"
                name={`children.${index}.name`}
                placeholder="Masukkan nama lengkap anak"
                value={child.name}
                onChange={(event) => setChild(index, 'name', event.target.value)}
                error={errors[`children.${index}.name`]}
              />
              <RadioGroupMobile
                label="Jenis Kelamin"
                name={`children.${child.key}.gender`}
                options={GENDERS}
                value={child.gender}
                onChange={(value) => setChild(index, 'gender', value)}
              />
              <DateTimeMobile
                label="Tanggal Lahir"
                name={`children.${index}.date_of_birth`}
                value={child.date_of_birth}
                onChange={(event) => setChild(index, 'date_of_birth', event.target.value)}
                error={errors[`children.${index}.date_of_birth`]}
              />
            </div>
          ))}

          {children.length < MAX_CHILDREN && (
            <Button type="button" className="btn-fill-white " onClick={addChild}>
              <Plus className="size-4 text-text-blue-100" />
              <span className="body1medium text-text-blue-100">Tambah informasi anak</span>
            </Button>
          )}
        </div>

        <Button type="submit" customButton="btn-fill-blue" disabled={saveProfile.isPending}>
          <Save />
          <span className="title2medium text-white">
            {saveProfile.isPending ? 'Menyimpan...' : 'Simpan'}
          </span>
        </Button>
      </form>
    </WrapperMobile>
  );
};

export default EditProfile;
